import type { TripDetails } from "../types";

export const SEGMENT_START_FLAG = 2;

// The tracker flags a segment start whenever it releases a batch of quarantined fixes, even when
// the released batch plainly continues the established track. Bridge a flagged break when the jump
// is achievable on foot for the elapsed time, capped so a long stationary pause cannot license an
// arbitrarily long straight line.
const BRIDGE_BASE_METERS = 25;
const BRIDGE_SPEED_METERS_PER_SECOND = 3;
const BRIDGE_MAX_METERS = 150;

const cache = new WeakMap<TripDetails, Uint8Array>();

function metersBetween(trip: TripDetails, from: number, to: number): number {
  const fromLatitude = (trip.positions[from * 2 + 1]! * Math.PI) / 180;
  const toLatitude = (trip.positions[to * 2 + 1]! * Math.PI) / 180;
  const latitudeDelta = toLatitude - fromLatitude;
  const longitudeDelta = ((trip.positions[to * 2]! - trip.positions[from * 2]!) * Math.PI) / 180;
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(fromLatitude) * Math.cos(toLatitude) * Math.sin(longitudeDelta / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

function bridgeable(trip: TripDetails, index: number): boolean {
  const distance = metersBetween(trip, index - 1, index);
  if (!Number.isFinite(distance)) return false;
  const elapsed = Math.max(0, trip.timestamps[index]! - trip.timestamps[index - 1]!);
  const allowance = Math.min(
    BRIDGE_BASE_METERS + BRIDGE_SPEED_METERS_PER_SECOND * elapsed,
    BRIDGE_MAX_METERS,
  );
  return distance <= allowance;
}

/**
 * Per-point mask of the segment starts worth drawing as a break, with spurious flags bridged away.
 * Entries are 0 or SEGMENT_START_FLAG, so the result substitutes directly for `trip.flags` in any
 * `& SEGMENT_START_FLAG` test.
 */
export function effectiveSegmentFlags(trip: TripDetails): Uint8Array {
  const cached = cache.get(trip);
  if (cached) return cached;
  const mask = new Uint8Array(trip.count);
  for (let index = 1; index < trip.count; index++)
    if (trip.flags[index]! & SEGMENT_START_FLAG && !bridgeable(trip, index)) mask[index] = SEGMENT_START_FLAG;
  cache.set(trip, mask);
  return mask;
}

export function segmentStartIndices(trip: TripDetails): Uint32Array {
  if (!trip.count) return new Uint32Array([0]);
  const mask = effectiveSegmentFlags(trip);
  const starts = [0];
  for (let index = 1; index < trip.count; index++) if (mask[index]! & SEGMENT_START_FLAG) starts.push(index);
  starts.push(trip.count);
  return new Uint32Array(starts);
}

export function tripSegments(trip: TripDetails): [number, number][][] {
  const mask = effectiveSegmentFlags(trip);
  const result: [number, number][][] = [];
  let current: [number, number][] = [];
  for (let index = 0; index < trip.count; index++) {
    if (index > 0 && mask[index]! & SEGMENT_START_FLAG) {
      if (current.length) result.push(current);
      current = [];
    }
    current.push([trip.positions[index * 2 + 1]!, trip.positions[index * 2]!]);
  }
  if (current.length) result.push(current);
  return result;
}
